"use client";

import Link from "next/link";
import { useId, useState, type CSSProperties } from "react";
import { ArrowUpRight } from "lucide-react";

import { ComparisonBrandIcon } from "@/components/site/primitives/comparison-brand-icon";
import { ComparisonCta } from "@/components/site/primitives/comparison-cta";
import {
  calculateTeamPrice,
  getComparisonCooperlyPrice,
  companyAnnualAmount,
  teamAnnualAmount,
  comparisonTeamSize,
} from "@/lib/comparison-pricing";
import type { ComparisonAudience, ComparisonSlug } from "@/lib/comparison-links";
import type { ComparisonEntry } from "@/lib/content/comparisons";
import { formatLongContentDate } from "@/lib/content/date-format";

import styles from "./comparison-pricing-panel.module.css";

type ComparisonPricingPanelProps = {
  slug: ComparisonSlug;
  audience: ComparisonAudience;
  competitor: string;
  pricing: ComparisonEntry["pricingComparison"];
  checkedAt: string;
};

export function ComparisonPricingPanel({
  slug,
  audience,
  competitor,
  pricing,
  checkedAt,
}: ComparisonPricingPanelProps) {
  const rangeId = useId();
  const [people, setPeople] = useState<number>(comparisonTeamSize.initial);
  const calculatedPrice = calculateTeamPrice(people, pricing.model);
  const cooperlyPrice = getComparisonCooperlyPrice(people);
  const progress = ((people - comparisonTeamSize.min) /
    (comparisonTeamSize.max - comparisonTeamSize.min)) * 100;

  return (
    <div className="mt-10 overflow-hidden rounded-[1.75rem] border border-border/70 bg-background">
      <div className="border-b border-border/70 bg-muted/20 px-5 py-6 md:px-9 md:py-7 lg:px-10">
        <p className="text-xl font-semibold tracking-tight md:text-2xl">
          Your plan stays flat as your team grows.
        </p>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          One flat price for this team size. Full Cooperly access included: up to 10 people at the Teams rate, then up to 50 people across five teams at the Company rate.
        </p>
        <div className="mt-6 grid gap-5 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center md:gap-10">
          <div className="flex items-center justify-between gap-5 md:block">
            <label htmlFor={rangeId} className="text-sm font-semibold">
              Company size
            </label>
            <output htmlFor={rangeId} className="text-3xl font-semibold tracking-tight md:mt-2 md:block">
              {people}<span className="ml-2 text-sm font-medium tracking-normal text-muted-foreground">people</span>
            </output>
          </div>
          <div>
            <input
              id={rangeId}
              type="range"
              min={comparisonTeamSize.min}
              max={comparisonTeamSize.max}
              step={1}
              value={people}
              aria-valuetext={`${people} people`}
              onChange={(event) => setPeople(Number(event.currentTarget.value))}
              className={styles.range}
              style={{ "--range-progress": `${progress}%` } as CSSProperties}
            />
            <div className="mt-1 flex items-center justify-between gap-4 text-xs text-muted-foreground">
              <span>{comparisonTeamSize.min}</span>
              <span className="text-center font-medium text-foreground/75">
                {people <= 10
                  ? `10 people: ${teamAnnualAmount}/year`
                  : `11–50 people: ${companyAnnualAmount}/year`}
              </span>
              <span>{comparisonTeamSize.max}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2" aria-live="polite" aria-atomic="true">
        <div className="min-w-0 bg-foreground px-4 py-6 text-background md:px-9 md:py-9 lg:px-10">
          <h3 className="text-base font-semibold tracking-tight md:text-xl">Cooperly</h3>
          <p className="mt-1 text-xs leading-5 text-white/65 md:text-sm">{cooperlyPrice.plan.name}</p>
          <div className={`${styles.alternativePrice} mt-5`}>
            <span
              data-initial-price={pricing.cooperlyAmount}
              className={`${styles.companyAmount} font-semibold leading-none tracking-tight`}
            >
              {cooperlyPrice.annualAmount}
            </span>
            <span className="mt-2 block text-xs text-white/65 md:text-sm">/ year · {cooperlyPrice.plan.name}</span>
          </div>
          <p className="mt-5 text-sm font-medium leading-6 text-white/90">
            <output htmlFor={rangeId}>{cooperlyPrice.monthlyEquivalent}</output>
            <span className="block text-xs font-normal leading-5 text-white/65 md:text-sm">per person / month equivalent</span>
          </p>
        </div>
        <div className="min-w-0 bg-muted/20 px-4 py-6 md:px-9 md:py-9 lg:px-10">
          <div className="flex items-center gap-2">
            <span className="flex size-8 shrink-0 items-center justify-center">
              <ComparisonBrandIcon slug={slug} />
            </span>
            <h3 className="text-base font-semibold tracking-tight md:text-xl">{competitor}</h3>
          </div>
          <p className="mt-1 text-xs leading-5 text-muted-foreground md:text-sm">{calculatedPrice.planLabel}</p>
          <div className={`${styles.alternativePrice} mt-5`}>
            <output htmlFor={rangeId} className={`${styles.amount} font-semibold leading-none tracking-tight text-foreground/80`}>
              <span data-initial-price={pricing.alternativeAmount}>
                {calculatedPrice.label}
              </span>
            </output>
            <span className="mt-2 block text-xs text-muted-foreground md:text-sm">{calculatedPrice.cadence}</span>
          </div>
          <p className="mt-5 text-xs leading-5 text-muted-foreground md:text-sm">{calculatedPrice.basis}</p>
        </div>
      </div>

      <div className="grid border-t border-border/70 md:grid-cols-2">
        <div className="bg-foreground p-5 text-background md:px-9 md:py-7 lg:px-10">
          <p className="max-w-xl text-sm leading-7 text-white/75 md:text-base">{cooperlyPrice.plan.description} {cooperlyPrice.plan.teamLimitLabel}, up to {cooperlyPrice.plan.maxTeamMembers} people. MCP and approved integrations are included.</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <div className="text-foreground">
              <ComparisonCta slug={slug} audience={audience} placement="pricing" secondary />
            </div>
            <Link href="/#pricing" className="inline-flex min-h-11 items-center text-sm font-medium text-white/75 underline decoration-white/35 underline-offset-4">
              See current plans
            </Link>
          </div>
        </div>
        <div className="min-w-0 bg-muted/20 p-5 md:px-9 md:py-7 lg:px-10">
          <p className="text-sm leading-7 text-muted-foreground md:text-base">{pricing.alternativeDetails}</p>
          <a href={pricing.sourceHref} className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-medium underline decoration-border underline-offset-4">
            {pricing.sourceLabel}<ArrowUpRight className="size-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="border-t border-border/70 px-5 py-6 md:px-9 lg:px-10">
        <p className="max-w-4xl text-lg font-medium leading-8 tracking-[-0.015em]">{pricing.comparison}</p>
        <p className="site-note mt-3">
          Prices checked <time dateTime={checkedAt}>{formatLongContentDate(checkedAt)}</time>.
          {pricing.model.kind === "quote" && " Competitor cost depends on your quote; no numerical saving is assumed."}
        </p>
        {pricing.exchangeNote && (
          <p className="site-note mt-2 max-w-4xl">
            {pricing.exchangeNote}{" "}
            <a href={pricing.exchangeSourceHref} className="font-medium underline decoration-border underline-offset-4">Rate source</a>
          </p>
        )}
      </div>
    </div>
  );
}
